import {
  COMBINED_KILN_OPTION_VALUE,
  mapToEquipmentOptionsWithCombinedKiln,
  getEquipmentDetailsByValue,
  usePlantEquipments
} from '@domain/kpis'
import {MultiSelect} from '@hconnect/common/components'
import {IconWithLabel} from '@hconnect/common/components/shiftEventLabels/IconWithLabel'
import {MULTI_SELECT_VALUE_SEPARATOR} from '@hconnect/common/consts'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, Stack, SxProps, Theme, Typography} from '@mui/material'
import {orderBy} from 'lodash'
import moment from 'moment-timezone'
import {useState, useMemo, useEffect, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {DowntimeByCategoryBarChart, EQUIPMENT_TYPE_ICON_MAP} from '../components'
import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../consts'
import {useStoppagesCategoryTypeCounts} from '../hooks'
import {StoppageSource} from '../types'
import {getStoppageCategoryCountPlaceHolders} from '../utils'

type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
}

const getRequestParams = (selectedEquipments: string[] | undefined = []) => {
  const pxTrendTags = selectedEquipments?.filter((x) => x !== COMBINED_KILN_OPTION_VALUE)
  const isKlinSelected = selectedEquipments?.length > pxTrendTags?.length
  const isPxTrendTagSelected = pxTrendTags.length > 0

  const stoppageSources: StoppageSource[] = []

  if (isKlinSelected) stoppageSources.unshift(StoppageSource.Janus)
  if (isPxTrendTagSelected) stoppageSources.unshift(StoppageSource.Cockpit)

  return {
    stoppageSources: stoppageSources,
    pxTrendTags: pxTrendTags
  }
}

export const DowntimeByCategoryChartContainer = ({
  plantId,
  cardBoxSx,
  placeholderWrapperSx
}: Props) => {
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)
  const handlePlantEquipmentsError = useCallback(() => {
    spawnErrorToast(t('message.failedToLoadEquipmentData'))
  }, [t])
  const {
    data: plantEquipments,
    isLoading: isLoadingEquipments,
    isError: isErrorEquipments,
    isRefetching: isRefetchingEquipments,
    refetch: refetchEquipments
  } = usePlantEquipments(plantId, {
    onError: handlePlantEquipmentsError
  })

  const timezone = useUpmPlantTimezone(plantId)
  const startOfTheYear = moment.tz(timezone).startOf('year')
  const endOfTheYear = moment.tz(timezone).endOf('year')
  const currentYear = startOfTheYear.year()

  const kilnLabel = t('downtimeByCategoryChart.label.kiln')
  const [equipments, setEquipments] = useState<string[] | undefined>([])

  const options = mapToEquipmentOptionsWithCombinedKiln(kilnLabel, plantEquipments)

  useEffect(() => {
    if (equipments?.length === 0 && options.length > 0) {
      setEquipments([options[0].value])
    }
  }, [options, equipments])

  const params = getRequestParams(equipments)
  const {
    data = [],
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useStoppagesCategoryTypeCounts(
    {
      plantId,
      from: startOfTheYear.toISOString(),
      to: endOfTheYear.toISOString(),
      ...params
    },
    {enabled: !!equipments?.length}
  )

  const dataWithMonthPlaceholders = useMemo(() => {
    const placeholders = getStoppageCategoryCountPlaceHolders(data, currentYear)
    return orderBy([...data, ...placeholders], 'month')
  }, [data, currentYear])

  return (
    <CardBox
      sx={{display: 'flex', flexDirection: 'column', ...cardBoxSx}}
      data-test-id="downtime-by-category-card"
    >
      <Box display="flex" mb={2}>
        <Stack flex={1}>
          <Typography variant="h3">{t('downtimeByCategoryChart.cardTitle')}</Typography>
          <Typography variant="caption">{t('downtimeByCategoryChart.cardSubtitle')}</Typography>
        </Stack>
        <Box
          sx={{
            ml: 3,
            mr: 3,
            width: {xs: '200px', lg: '250px'},
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <MultiSelect
            value={equipments}
            label={t('downtimeByCategoryChart.label.asset')}
            onChange={setEquipments}
            options={options}
            data-test-id="downtime-equipment-select"
            getOptionLabel={(option) => {
              const optionEquipmentType = getEquipmentDetailsByValue(
                option.value,
                kilnLabel,
                plantEquipments
              ).equipmentType
              return (
                <IconWithLabel
                  icon={<>{EQUIPMENT_TYPE_ICON_MAP[optionEquipmentType]}</>}
                  iconOnly={false}
                  label={option.label}
                />
              )
            }}
            renderValue={(values) =>
              values
                .map(
                  (value) =>
                    getEquipmentDetailsByValue(value, kilnLabel, plantEquipments).description
                )
                .join(MULTI_SELECT_VALUE_SEPARATOR)
            }
          />
        </Box>
        <Chip icon={<CalendarTodayOutlined />} label={currentYear} size="small" sx={{mx: 1}} />
      </Box>
      <DataContentWrapper
        showLoader={isLoading || isLoadingEquipments}
        isError={isError || isErrorEquipments}
        isRefetching={isRefetching || isRefetchingEquipments}
        data={data}
        retryFunction={() => {
          if (isError) void refetch()
          if (isErrorEquipments) void refetchEquipments()
        }}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
      >
        <Box sx={{flex: 1, display: 'flex'}}>
          <DowntimeByCategoryBarChart
            data={dataWithMonthPlaceholders}
            labelVariant={'threeLetters'}
            abbreviatedLegendLabels={false}
          />
        </Box>
      </DataContentWrapper>
    </CardBox>
  )
}
